import { AnyAction, Reducer } from "redux";
import { createPatch } from "rfc6902";
import { IAppThunkAction } from ".";
import AuctionApi from "../api/AuctionApi";
import FileApi from "../api/FileApi";
import IErrorInfo from "../models/account/IErrorInfo";
import IAuction from "../models/auction/IAuction";
import IAuctionDetail from "../models/auction/IAuctionDetail";
import ICreateUpdateAuction from "../models/auction/ICreateUpdateAuction";
import { FileType } from "../models/files/FileType";
import IFileMetadata from "../models/files/IFileMetadata";
import { LoadState } from "../models/utility/LoadState";

export interface IAuctionStoreState {
    auctionDetail: IAuctionDetail | null;
    auctionDetailLoadState: LoadState;
    auctions: IAuction[];
    auctionsLoadState: LoadState;
    metadataHomeLoadState: LoadState;
    metadataMapLoadState: LoadState;
    metadataPictureLoadState: LoadState;
    savingAuction: boolean;
    savingAuctionErrors: IErrorInfo[];
}

export type KnownAction =
    | IToggleAuctionDetailLoadState
    | IToggleAuctionListLoadState
    | IToggleAuctionLiveStatus
    | IToggleSavingAuction
    | IToggleMetadataHomeLoadState
    | IToggleMetadataMapLoadState
    | IToggleMetadataPictureLoadState
    | IUpdateAuctionDetail
    | IUpdateAuctionList
    | IUpdateHomeMetadata
    | IUpdateMapMetadata
    | IUpdatePictureMetadata;

export interface IToggleAuctionDetailLoadState {
    type: "TOGGLE_AUCTION_DETAIL_LOAD_STATE";
    auctionDetailLoadState: LoadState;
}
export interface IToggleAuctionListLoadState {
    type: "TOGGLE_AUCTION_LIST_LOAD_STATE";
    auctionsLoadState: LoadState;
}
export interface IToggleAuctionLiveStatus {
    type: "TOGGLE_AUCTION_LIVE_STATUS";
    auctionId: string;
    live: boolean;
    completed: boolean;
}
export interface IToggleSavingAuction {
    type: "TOGGLE_SAVING_AUCTION";
    savingAuction: boolean;
    savingAuctionErrors: IErrorInfo[];
}
export interface IToggleMetadataHomeLoadState {
    type: "TOGGLE_METADATA_HOME_LOAD_STATE";
    metadataHomeLoadState: LoadState;
}
export interface IToggleMetadataMapLoadState {
    type: "TOGGLE_METADATA_MAP_LOAD_STATE";
    metadataMapLoadState: LoadState;
}
export interface IToggleMetadataPictureLoadState {
    type: "TOGGLE_METADATA_PICTURE_LOAD_STATE";
    metadataPictureLoadState: LoadState;
}
export interface IUpdateAuctionDetail {
    type: "UPDATE_AUCTION_DETAIL";
    auctionDetail: IAuctionDetail | null;
    auctionDetailLoadState: LoadState;
}
export interface IUpdateAuctionList {
    type: "UPDATE_AUCTION_LIST";
    auctions: IAuction[];
    auctionsLoadState: LoadState;
}
export interface IUpdateHomeMetadata {
    type: "UPDATE_HOME_METADATA";
    homeMetadata: IFileMetadata[];
}
export interface IUpdateMapMetadata {
    type: "UPDATE_MAP_METADATA";
    mapMetadata: IFileMetadata[];
}
export interface IUpdatePictureMetadata {
    type: "UPDATE_PICTURE_METADATA";
    pictureMetadata: IFileMetadata[];
    thumbMetadata: IFileMetadata[];
}

export const actionCreators = {
    clearAuctionDetail: (): IAppThunkAction => (dispatch) => {
        dispatch({
            type: "UPDATE_AUCTION_DETAIL",
            auctionDetail: null,
            auctionDetailLoadState: LoadState.NotLoaded
        });
    },
    createAuction:
        (newAuction: ICreateUpdateAuction): IAppThunkAction =>
        async (dispatch) => {
            dispatch({
                type: "TOGGLE_SAVING_AUCTION",
                savingAuction: true,
                savingAuctionErrors: []
            });

            const errors = await AuctionApi.CreateAuction(newAuction);
            dispatch({
                type: "TOGGLE_SAVING_AUCTION",
                savingAuction: false,
                savingAuctionErrors: errors
            });
        },
    deleteAuction:
        (auctionId: string): IAppThunkAction =>
        async (dispatch) => {
            dispatch({
                type: "TOGGLE_SAVING_AUCTION",
                savingAuction: true,
                savingAuctionErrors: []
            });

            const errors = await AuctionApi.DeleteAuction(auctionId);
            dispatch({
                type: "TOGGLE_SAVING_AUCTION",
                savingAuction: false,
                savingAuctionErrors: errors
            });
        },
    getLatestAuctionInfo: (): IAppThunkAction => async (dispatch, getState) => {
        const state = getState();
        const auctionId = state.parcelStoreState.auctionInfo.auctionId;

        if (auctionId) {
            const currentState = state.parcelStoreState.parcels.map((p) => {
                return {
                    endDate: p.auctionEndDate,
                    isActive: p.isActive,
                    parcelId: p.parcelId,
                    startDate: p.auctionStartDate
                };
            });

            const result = await AuctionApi.GetLiveAuctionInformation(
                auctionId,
                currentState
            );
            let latestMessage = "";

            result.forEach((p) => {
                latestMessage = latestMessage ? latestMessage : p.updateMessage;

                dispatch({ type: "UPDATE_LIVE_PARCEL_BIDS", auctionStatus: p });
                dispatch({
                    type: "UPDATE_LIVE_PARCEL_INFORMATION",
                    auctionStatus: p
                });
            });

            if (latestMessage) {
                dispatch({
                    type: "SHOW_PARCEL_UPDATED_SNACKBAR_MESSAGE",
                    auctionId,
                    message: latestMessage
                });
                dispatch({ type: "HIDE_PARCEL_UPDATED_SNACKBAR_MESSAGE" });
            }
        }
    },
    loadAuctionDetail:
        (url: string): IAppThunkAction =>
        async (dispatch) => {
            dispatch({
                type: "TOGGLE_AUCTION_DETAIL_LOAD_STATE",
                auctionDetailLoadState: LoadState.Loading
            });

            const auction = await AuctionApi.GetAuction(url);
            dispatch({
                type: "UPDATE_AUCTION_DETAIL",
                auctionDetail: auction,
                auctionDetailLoadState: LoadState.Loaded
            });
        },

    loadAuctionList:
        (includeDrafts = false): IAppThunkAction =>
        async (dispatch) => {
            dispatch({
                type: "TOGGLE_AUCTION_LIST_LOAD_STATE",
                auctionsLoadState: LoadState.Loading
            });

            const auctions = await AuctionApi.GetAuctions(includeDrafts);
            dispatch({
                type: "UPDATE_AUCTION_LIST",
                auctions,
                auctionsLoadState: LoadState.Loaded
            });
        },
    refreshAuctionFileMetadata:
        (fileType: FileType): IAppThunkAction =>
        async (dispatch, getState) => {
            const auction = getState().auctionStoreState.auctionDetail;
            if (!auction) {
                return;
            }

            // choose dispatch based on file type
            if (fileType === FileType.AuctionMap) {
                dispatch({
                    type: "TOGGLE_METADATA_MAP_LOAD_STATE",
                    metadataMapLoadState: LoadState.Loading
                });

                const mapMetadata = await FileApi.GetFileMetadata(
                    auction.auctionId,
                    fileType
                );

                dispatch({ type: "UPDATE_MAP_METADATA", mapMetadata });
            } else if (fileType === FileType.AuctionPicture) {
                dispatch({
                    type: "TOGGLE_METADATA_PICTURE_LOAD_STATE",
                    metadataPictureLoadState: LoadState.Loading
                });

                const pictureMetadata = await FileApi.GetFileMetadata(
                    auction.auctionId,
                    fileType
                );
                const thumbMetadata = await FileApi.GetFileMetadata(
                    auction.auctionId,
                    FileType.AuctionPictureThumbnail
                );

                dispatch({
                    type: "UPDATE_PICTURE_METADATA",
                    pictureMetadata,
                    thumbMetadata
                });
            } else if (fileType === FileType.AuctionHomePicture) {
                dispatch({
                    type: "TOGGLE_METADATA_HOME_LOAD_STATE",
                    metadataHomeLoadState: LoadState.Loading
                });

                const homeMetadata = await FileApi.GetFileMetadata(
                    auction.auctionId,
                    fileType
                );

                dispatch({ type: "UPDATE_HOME_METADATA", homeMetadata });
            }
        },
    resetAuctionSaveErrors: (): IAppThunkAction => (dispatch) => {
        dispatch({
            type: "TOGGLE_SAVING_AUCTION",
            savingAuction: false,
            savingAuctionErrors: []
        });
    },
    toggleAuctionLoadState:
        (auctionDetailLoadState: LoadState): IAppThunkAction =>
        (dispatch) => {
            dispatch({
                type: "TOGGLE_AUCTION_DETAIL_LOAD_STATE",
                auctionDetailLoadState
            });
        },
    toggleAuctionMetadataLoadState:
        (fileType: FileType, loadState: LoadState): IAppThunkAction =>
        (dispatch) => {
            // choose dispatch based on file type
            if (fileType === FileType.AuctionMap) {
                dispatch({
                    type: "TOGGLE_METADATA_MAP_LOAD_STATE",
                    metadataMapLoadState: loadState
                });
            } else if (fileType === FileType.AuctionPicture) {
                dispatch({
                    type: "TOGGLE_METADATA_PICTURE_LOAD_STATE",
                    metadataPictureLoadState: loadState
                });
            } else if (fileType === FileType.AuctionHomePicture) {
                dispatch({
                    type: "TOGGLE_METADATA_HOME_LOAD_STATE",
                    metadataHomeLoadState: loadState
                });
            }
        },
    updateAuction:
        (updatedAuction: ICreateUpdateAuction): IAppThunkAction =>
        async (dispatch, getState) => {
            // generate patch for update
            const auction = getState().auctionStoreState.auctionDetail;
            if (auction) {
                const oldAuction = {
                    draftMode: auction.draftMode,
                    name: auction.name,
                    url: auction.url,
                    isSynchronized: auction.isSynchronized,
                    isApprovalRequired: auction.isApprovalRequired,
                    approvedAuctionBidderUserIds:
                        auction.approvedAuctionBidderUserIds,
                    synchronizedCloseTime: auction.synchronizedCloseTime ?? "",
                    synchronizedStartTime: auction.synchronizedStartTime ?? ""
                } as ICreateUpdateAuction;

                const patch = createPatch(oldAuction, updatedAuction);
                if (patch.length === 0) {
                    dispatch({
                        type: "TOGGLE_SAVING_AUCTION",
                        savingAuction: true,
                        savingAuctionErrors: []
                    });
                    const errors = await AuctionApi.SyncSalesSite(
                        auction.auctionId
                    );
                    dispatch({
                        type: "TOGGLE_SAVING_AUCTION",
                        savingAuction: false,
                        savingAuctionErrors: errors
                    });
                    return;
                }

                dispatch({
                    type: "TOGGLE_SAVING_AUCTION",
                    savingAuction: true,
                    savingAuctionErrors: []
                });

                const errors = await AuctionApi.UpdateAuction(
                    auction.auctionId,
                    patch
                );
                dispatch({
                    type: "TOGGLE_SAVING_AUCTION",
                    savingAuction: false,
                    savingAuctionErrors: errors
                });
            }
        }
};

export const reducer: Reducer<IAuctionStoreState> = (
    state: IAuctionStoreState | undefined,
    incomingAction: AnyAction
) => {
    const action = incomingAction as KnownAction;
    const prevState = state || defaultState;

    switch (action.type) {
        case "TOGGLE_AUCTION_DETAIL_LOAD_STATE": {
            return {
                auctionDetail: prevState.auctionDetail,
                auctionDetailLoadState: action.auctionDetailLoadState,
                auctions: prevState.auctions,
                auctionsLoadState: prevState.auctionsLoadState,
                metadataHomeLoadState: prevState.metadataHomeLoadState,
                metadataMapLoadState: prevState.metadataMapLoadState,
                metadataPictureLoadState: prevState.metadataPictureLoadState,
                savingAuction: prevState.savingAuction,
                savingAuctionErrors: prevState.savingAuctionErrors
            };
        }
        case "TOGGLE_AUCTION_LIST_LOAD_STATE": {
            return {
                auctionDetail: prevState.auctionDetail,
                auctionDetailLoadState: prevState.auctionDetailLoadState,
                auctions: prevState.auctions,
                auctionsLoadState: action.auctionsLoadState,
                metadataHomeLoadState: prevState.metadataHomeLoadState,
                metadataMapLoadState: prevState.metadataMapLoadState,
                metadataPictureLoadState: prevState.metadataPictureLoadState,
                savingAuction: prevState.savingAuction,
                savingAuctionErrors: prevState.savingAuctionErrors
            };
        }
        case "TOGGLE_AUCTION_LIVE_STATUS": {
            const auctions = prevState.auctions.map((e) => {
                if (e.auctionId !== action.auctionId) {
                    return e;
                }

                return {
                    ...e,
                    isCompleted: action.completed,
                    isLive: action.live
                };
            });

            let auctionDetail = prevState.auctionDetail;
            if (auctionDetail && auctionDetail.auctionId === action.auctionId) {
                auctionDetail = {
                    ...prevState.auctionDetail,
                    isCompleted: action.completed,
                    isLive: action.live
                } as IAuctionDetail;
            }

            return {
                auctionDetail,
                auctionDetailLoadState: prevState.auctionDetailLoadState,
                auctions,
                auctionsLoadState: prevState.auctionsLoadState,
                metadataHomeLoadState: prevState.metadataHomeLoadState,
                metadataMapLoadState: prevState.metadataMapLoadState,
                metadataPictureLoadState: prevState.metadataPictureLoadState,
                savingAuction: prevState.savingAuction,
                savingAuctionErrors: prevState.savingAuctionErrors
            };
        }
        case "TOGGLE_METADATA_HOME_LOAD_STATE": {
            return {
                auctionDetail: prevState.auctionDetail,
                auctionDetailLoadState: prevState.auctionDetailLoadState,
                auctions: prevState.auctions,
                auctionsLoadState: prevState.auctionsLoadState,
                metadataHomeLoadState: action.metadataHomeLoadState,
                metadataMapLoadState: prevState.metadataMapLoadState,
                metadataPictureLoadState: prevState.metadataPictureLoadState,
                savingAuction: prevState.savingAuction,
                savingAuctionErrors: prevState.savingAuctionErrors
            };
        }
        case "TOGGLE_METADATA_MAP_LOAD_STATE": {
            return {
                auctionDetail: prevState.auctionDetail,
                auctionDetailLoadState: prevState.auctionDetailLoadState,
                auctions: prevState.auctions,
                auctionsLoadState: prevState.auctionsLoadState,
                metadataHomeLoadState: prevState.metadataHomeLoadState,
                metadataMapLoadState: action.metadataMapLoadState,
                metadataPictureLoadState: prevState.metadataPictureLoadState,
                savingAuction: prevState.savingAuction,
                savingAuctionErrors: prevState.savingAuctionErrors
            };
        }
        case "TOGGLE_METADATA_PICTURE_LOAD_STATE": {
            return {
                auctionDetail: prevState.auctionDetail,
                auctionDetailLoadState: prevState.auctionDetailLoadState,
                auctions: prevState.auctions,
                auctionsLoadState: prevState.auctionsLoadState,
                metadataHomeLoadState: prevState.metadataHomeLoadState,
                metadataMapLoadState: prevState.metadataMapLoadState,
                metadataPictureLoadState: action.metadataPictureLoadState,
                savingAuction: prevState.savingAuction,
                savingAuctionErrors: prevState.savingAuctionErrors
            };
        }
        case "TOGGLE_SAVING_AUCTION": {
            return {
                auctionDetail: prevState.auctionDetail,
                auctionDetailLoadState: prevState.auctionDetailLoadState,
                auctions: prevState.auctions,
                auctionsLoadState: prevState.auctionsLoadState,
                metadataHomeLoadState: prevState.metadataHomeLoadState,
                metadataMapLoadState: prevState.metadataMapLoadState,
                metadataPictureLoadState: prevState.metadataPictureLoadState,
                savingAuction: action.savingAuction,
                savingAuctionErrors: action.savingAuctionErrors
            };
        }
        case "UPDATE_AUCTION_DETAIL": {
            return {
                auctionDetail: action.auctionDetail,
                auctionDetailLoadState: action.auctionDetailLoadState,
                auctions: prevState.auctions,
                auctionsLoadState: prevState.auctionsLoadState,
                metadataHomeLoadState: prevState.metadataHomeLoadState,
                metadataMapLoadState: prevState.metadataMapLoadState,
                metadataPictureLoadState: prevState.metadataPictureLoadState,
                savingAuction: prevState.savingAuction,
                savingAuctionErrors: prevState.savingAuctionErrors
            };
        }
        case "UPDATE_AUCTION_LIST": {
            return {
                auctionDetail: prevState.auctionDetail,
                auctionDetailLoadState: prevState.auctionDetailLoadState,
                auctions: action.auctions,
                auctionsLoadState: action.auctionsLoadState,
                metadataHomeLoadState: prevState.metadataHomeLoadState,
                metadataMapLoadState: prevState.metadataMapLoadState,
                metadataPictureLoadState: prevState.metadataPictureLoadState,
                savingAuction: prevState.savingAuction,
                savingAuctionErrors: prevState.savingAuctionErrors
            };
        }
        case "UPDATE_HOME_METADATA": {
            const auctionDetail = {
                ...prevState.auctionDetail,
                homePictureMetadata:
                    action.homeMetadata.length > 0
                        ? action.homeMetadata[0]
                        : null
            } as IAuctionDetail;

            return {
                auctionDetail,
                auctionDetailLoadState: prevState.auctionDetailLoadState,
                auctions: prevState.auctions,
                auctionsLoadState: prevState.auctionsLoadState,
                metadataHomeLoadState: LoadState.Loaded,
                metadataMapLoadState: prevState.metadataMapLoadState,
                metadataPictureLoadState: prevState.metadataPictureLoadState,
                savingAuction: prevState.savingAuction,
                savingAuctionErrors: prevState.savingAuctionErrors
            };
        }
        case "UPDATE_MAP_METADATA": {
            const auctionDetail = {
                ...prevState.auctionDetail,
                mapMetadata:
                    action.mapMetadata.length > 0 ? action.mapMetadata[0] : null
            } as IAuctionDetail;

            return {
                auctionDetail,
                auctionDetailLoadState: prevState.auctionDetailLoadState,
                auctions: prevState.auctions,
                auctionsLoadState: prevState.auctionsLoadState,
                metadataHomeLoadState: prevState.metadataHomeLoadState,
                metadataMapLoadState: LoadState.Loaded,
                metadataPictureLoadState: prevState.metadataPictureLoadState,
                savingAuction: prevState.savingAuction,
                savingAuctionErrors: prevState.savingAuctionErrors
            };
        }
        case "UPDATE_PICTURE_METADATA": {
            const auctionDetail = {
                ...prevState.auctionDetail,
                pictureMetadata: action.pictureMetadata,
                thumbnailMetadata: action.thumbMetadata
            } as IAuctionDetail;

            return {
                auctionDetail,
                auctionDetailLoadState: prevState.auctionDetailLoadState,
                auctions: prevState.auctions,
                auctionsLoadState: prevState.auctionsLoadState,
                metadataHomeLoadState: prevState.metadataHomeLoadState,
                metadataMapLoadState: prevState.metadataMapLoadState,
                metadataPictureLoadState: LoadState.Loaded,
                savingAuction: prevState.savingAuction,
                savingAuctionErrors: prevState.savingAuctionErrors
            };
        }
        default: {
            break;
        }
    }

    return prevState;
};

export const defaultState = {
    auctionDetail: null,
    auctionDetailLoadState: LoadState.NotLoaded,
    auctions: [],
    auctionsLoadState: LoadState.NotLoaded,
    metadataHomeLoadState: LoadState.NotLoaded,
    metadataMapLoadState: LoadState.NotLoaded,
    metadataPictureLoadState: LoadState.NotLoaded,
    savingAuction: false,
    savingAuctionErrors: []
} as IAuctionStoreState;
